/*
 * LICENSE PROTECTED SOFTWARE
 *
 * Bu yazilim lisans korumasi altindadir.
 * Gecerli lisans olmadan kullanilmasi yasaktir.
 *
 * Kaynak kodunun izinsiz erisimi, kopyalanmasi veya dagitimi
 * hukuki yaptirimslara tabidir.
 * (c) 2026  @cemdemirci0101
 *  bilgi@cemdemirci.com
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution
 * of this file, via any medium, is strictly prohibited.
 * (c) 2026 All Rights Reserved.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { usersAPI, locationsAPI, organsAPI } from '../lib/api';
import { formatDateTime, USER_ROLES, formatApiErrorDetail } from '../lib/utils';
import DOMPurify from 'dompurify';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Users, Search, Filter, Eye, Edit, Trash2, ChevronLeft, ChevronRight,
  Loader2, Shield, Mail, Phone, MapPin, Calendar, CreditCard, User,
  X, Download, FileText, Building2, GraduationCap, Send, Clock, Briefcase, UserCheck,
} from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';

const CINSIYET_MAP = { erkek: 'Erkek', kadin: 'Kadin', belirtmek_istemiyor: 'Belirtmek Istemiyor' };
const EGITIM_MAP = { ilkokul: 'Ilkokul', ortaokul: 'Ortaokul', lise: 'Lise', onlisans: 'Onlisans', lisans: 'Lisans', yuksek_lisans: 'Yuksek Lisans' };

// Kullanici Detay Dialog
const UserDetailDialog = ({ user: u, open, onClose, onEdit, iller, isAdmin }) => {
  const [ilName, setIlName] = useState('');
  const [ilceName, setIlceName] = useState('');
  const [mahalleName, setMahalleName] = useState('');

  useEffect(() => {
    if (!u) return;
    const loadNames = async () => {
      try {
        if (u.il_id) {
          const r = await locationsAPI.getIller();
          const il = r.data.find(i => i.id === u.il_id);
          setIlName(il?.name || u.il_id);
        }
        if (u.ilce_id && u.il_id) {
          const r = await locationsAPI.getIlceler(u.il_id);
          const ilce = r.data.find(i => i.id === u.ilce_id);
          setIlceName(ilce?.name || u.ilce_id);
        }
        if (u.mahalle_id && u.ilce_id) {
          const r = await locationsAPI.getMahalleler(u.ilce_id);
          const m = r.data.find(i => i.id === u.mahalle_id);
          setMahalleName(m?.name || u.mahalle_id);
        }
      } catch { /* silent */ }
    };
    loadNames();
  }, [u]);

  if (!u) return null;

  const roleInfo = USER_ROLES[u.role] || { label: u.role };

  const InfoRow = ({ icon: Icon, label, value, className = '' }) => (
    value ? (
      <div className={`flex items-start gap-3 py-2.5 ${className}`}>
        <Icon className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-xs text-slate-400 uppercase tracking-wide">{label}</p>
          <p className="text-sm text-slate-800 mt-0.5 break-words">{value}</p>
        </div>
      </div>
    ) : null
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader className="pb-0">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shrink-0">
              <span className="text-lg font-bold text-white">{u.name?.charAt(0)}{u.surname?.charAt(0)}</span>
            </div>
            <div className="min-w-0">
              <DialogTitle className="text-lg">{u.name} {u.surname}</DialogTitle>
              <DialogDescription className="flex items-center gap-2 mt-0.5">
                <Badge className="text-xs">{roleInfo.label}</Badge>
                <Badge variant={u.is_active ? 'outline' : 'secondary'} className={u.is_active ? 'border-emerald-200 text-emerald-700 bg-emerald-50 text-xs' : 'text-xs'}>
                  {u.is_active ? 'Aktif' : 'Pasif'}
                </Badge>
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="divide-y divide-slate-100 mt-2">
          <InfoRow icon={CreditCard} label="TC Kimlik No" value={u.tc_kimlik} />
          <InfoRow icon={Mail} label="E-posta" value={u.email} />
          <InfoRow icon={Phone} label="Telefon" value={u.phone} />
          <InfoRow icon={Calendar} label="Dogum Tarihi" value={u.dogum_tarihi ? new Date(u.dogum_tarihi).toLocaleDateString('tr-TR') : null} />
          <InfoRow icon={User} label="Cinsiyet" value={CINSIYET_MAP[u.cinsiyet]} />
          <InfoRow icon={GraduationCap} label="Egitim Durumu" value={EGITIM_MAP[u.egitim_durumu]} />
          <InfoRow icon={Briefcase} label="Meslek" value={u.meslek} />
          <InfoRow icon={Building2} label="Mezun/Devam Edilen Okul" value={u.okul_adi} />
          <InfoRow icon={Building2} label="Uyesi/Gonullusu Oldugu Kurulus" value={u.uyesi_oldugu_kurulus} />
          <InfoRow icon={MapPin} label="Il / Ilce / Mahalle" value={[ilName, ilceName, mahalleName].filter(Boolean).join(' / ') || null} />
          <InfoRow icon={MapPin} label="Acik Adres" value={u.acik_adres} />
          <InfoRow icon={Calendar} label="Kayit Tarihi" value={formatDateTime(u.created_at)} />
          {/* Organ Atamasi */}
          {(u.organ_assignments || []).length > 0 && (
            <div className="py-2.5">
              <div className="flex items-start gap-3">
                <Building2 className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-slate-400 uppercase tracking-wide">Organ Atamasi</p>
                  <div className="mt-1 space-y-1">
                    {(u.organ_assignments || []).map((a, i) => {
                      const label = a.type === 'meclis'
                        ? (a.role === 'baskan' ? 'Meclis Baskani' : 'Meclis Uyesi')
                        : a.type === 'calisma_grubu'
                        ? (a.role === 'baskan' ? 'CG Baskani' : 'CG Uyesi')
                        : a.type === 'yurutme_kurulu'
                        ? (a.role === 'baskan' ? 'YK Baskani' : 'Yurutme Kurulu Uyesi')
                        : a.type;
                      const cls = a.role === 'uye'
                        ? 'border-emerald-200 text-emerald-700 bg-emerald-50'
                        : 'border-blue-200 text-blue-700 bg-blue-50';
                      return (
                        <div key={a.id || `assign-${i}`} className="flex items-center gap-1.5">
                          <Badge variant="outline" className={`text-xs ${cls}`}>
                            {label}
                          </Badge>
                          <span className="text-sm text-slate-800">{a.name}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* E-posta Dogrulama Durumu */}
          <div className="py-2.5">
            <div className="flex items-start gap-3">
              <Mail className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-xs text-slate-400 uppercase tracking-wide">E-posta Dogrulama</p>
                <div className="flex items-center gap-1.5 mt-1">
                  <span className={`w-2 h-2 rounded-full shrink-0 ${u.is_verified ? 'bg-emerald-500' : 'bg-red-500'}`} />
                  <span className={`text-sm font-medium ${u.is_verified ? 'text-emerald-700' : 'text-red-600'}`}>
                    {u.is_verified ? 'Dogrulandi' : 'Dogrulanmadi'}
                  </span>
                </div>
              </div>
            </div>
          </div>
          {/* KVKK Onay Bilgisi */}
          <div className="py-2.5">
            <div className="flex items-start gap-3">
              <Shield className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-xs text-slate-400 uppercase tracking-wide">KVKK Onay</p>
                {u.kvkk_onay ? (
                  <div className="mt-1 space-y-0.5">
                    <div className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                      <span className="text-sm text-emerald-700 font-medium">Onaylandi</span>
                    </div>
                    {u.kvkk_onay_tarihi && <p className="text-xs text-slate-500">Tarih: {formatDateTime(u.kvkk_onay_tarihi)}</p>}
                    {u.kvkk_onay_ip && <p className="text-xs text-slate-500">IP: {u.kvkk_onay_ip}</p>}
                  </div>
                ) : (
                  <p className="text-sm text-red-500 mt-0.5">Onaylanmamis</p>
                )}
              </div>
            </div>
          </div>
          {/* Iletisim Izni Bilgisi */}
          <div className="py-2.5 border-t border-slate-100">
            <div className="flex items-start gap-3">
              <Mail className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-xs text-slate-400 uppercase tracking-wide">Iletisim Izni</p>
                {u.iletisim_izni && (Object.values(u.iletisim_izni).some(v => v)) ? (
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {u.iletisim_izni.email && <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">E-posta</span>}
                    {u.iletisim_izni.sms && <span className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded">SMS</span>}
                    {u.iletisim_izni.voice && <span className="text-xs bg-purple-50 text-purple-700 px-2 py-0.5 rounded">Sesli Arama</span>}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 mt-0.5">Izin verilmemis</p>
                )}
                {/* Iletisim Izni Gecmisi */}
                {u.iletisim_izni_gecmisi && u.iletisim_izni_gecmisi.length > 0 && (
                  <div className="mt-2 space-y-1 border-t border-slate-50 pt-2">
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider">Izin Degisiklik Gecmisi</p>
                    {u.iletisim_izni_gecmisi.slice(-10).reverse().map((log, i) => (
                      <div key={i} className="text-xs text-slate-500 flex items-center gap-1.5">
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${log.yeni ? 'bg-emerald-500' : 'bg-red-400'}`} />
                        <span className="font-medium">{log.kanal}</span>
                        <span>{log.yeni ? 'izin verildi' : 'izin kaldirildi'}</span>
                        <span className="text-slate-400">| {formatDateTime(log.tarih)}</span>
                        {log.ip && <span className="text-slate-300">IP: {log.ip}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="mt-2">
          {isAdmin && (
            <Button size="sm" onClick={() => { onClose(false); onEdit(u); }} data-testid="detail-edit-btn">
              <Edit className="w-4 h-4 mr-1.5" /> Duzenle
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => onClose(false)}>Kapat</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Kullanici Duzenleme Dialog
const UserEditDialog = ({ user: u, open, onClose, onSaved, iller }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [ilceler, setIlceler] = useState([]);
  const [mahalleler, setMahalleler] = useState([]);
  const [meclisler, setMeclisler] = useState([]);
  const [allCgGroups, setAllCgGroups] = useState([]);
  const [allYKs, setAllYKs] = useState([]);  // Tum meclis-alti YK organlari
  const [selectedMeclis, setSelectedMeclis] = useState('');
  const [selectedCG, setSelectedCG] = useState('');
  const [selectedYK, setSelectedYK] = useState('');

  useEffect(() => {
    if (u) {
      setForm({
        name: u.name || '', surname: u.surname || '', phone: u.phone || '',
        tc_kimlik: u.tc_kimlik || '', dogum_tarihi: u.dogum_tarihi || '',
        acik_adres: u.acik_adres || '', cinsiyet: u.cinsiyet || '', egitim_durumu: u.egitim_durumu || '',
        meslek: u.meslek || '',
        okul_adi: u.okul_adi || '',
        uyesi_oldugu_kurulus: u.uyesi_oldugu_kurulus || '',
        il_id: u.il_id || '', ilce_id: u.ilce_id || '', mahalle_id: u.mahalle_id || '',
        role: u.role || 'vatandas', is_active: u.is_active ?? true,
      });
      if (u.il_id) locationsAPI.getIlceler(u.il_id).then(r => setIlceler(r.data)).catch(() => {});
      if (u.ilce_id) locationsAPI.getMahalleler(u.ilce_id).then(r => setMahalleler(r.data)).catch(() => {});

      // Meclis, CG ve YK listelerini bagimsiz yukle
      organsAPI.getAll({ type: 'meclis' }).then(r => setMeclisler(r.data)).catch(() => {});
      organsAPI.getAll({ type: 'calisma_grubu' }).then(r => setAllCgGroups(r.data)).catch(() => {});
      organsAPI.getAll({ type: 'yurutme_kurulu' }).then(r => setAllYKs(r.data)).catch(() => {});

      // Mevcut organ atamasini bul
      const assignments = u.organ_assignments || [];
      const meclisAssign = assignments.find(a => a.type === 'meclis' && a.role === 'baskan');
      const cgAssign = assignments.find(a => a.type === 'calisma_grubu' && a.role === 'baskan');
      const ykAssign = assignments.find(a => a.type === 'yurutme_kurulu' && a.role === 'uye');
      setSelectedMeclis(meclisAssign?.id || '');
      setSelectedCG(cgAssign?.id || '');
      setSelectedYK(ykAssign?.id || '');
    }
  }, [u]);

  const handleIlChange = async (v) => {
    setForm(f => ({ ...f, il_id: v, ilce_id: '', mahalle_id: '' }));
    setMahalleler([]);
    try { const r = await locationsAPI.getIlceler(v); setIlceler(r.data); } catch { setIlceler([]); }
  };

  const handleIlceChange = async (v) => {
    setForm(f => ({ ...f, ilce_id: v, mahalle_id: '' }));
    try { const r = await locationsAPI.getMahalleler(v); setMahalleler(r.data); } catch { setMahalleler([]); }
  };

  const handleRoleChange = (v) => {
    setForm(f => ({ ...f, role: v }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      Object.entries(form).forEach(([k, v]) => {
        if (v !== '' && v !== null && v !== undefined) payload[k] = v;
      });

      // Meclis ve CG baskanligi - her zaman gonder (temizleme icin de)
      payload.organ_id = selectedMeclis || '';
      payload.cg_organ_id = selectedCG || '';
      payload.yk_organ_id = selectedYK || '';

      await usersAPI.update(u.id, payload);
      toast.success('Kullanici guncellendi');
      onClose(false);
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Guncelleme basarisiz'); }
    finally { setSaving(false); }
  };

  if (!u) return null;

  // CG'leri meclis bazinda grupla (gosterim icin)
  const cgByMeclis = {};
  allCgGroups.forEach(cg => {
    const parentName = meclisler.find(m => m.id === cg.parent_id)?.name || 'Diger';
    if (!cgByMeclis[parentName]) cgByMeclis[parentName] = [];
    cgByMeclis[parentName].push(cg);
  });

  // YK'lari meclis bazinda grupla (gosterim icin)
  const ykByMeclis = {};
  allYKs.forEach(yk => {
    const parent = meclisler.find(m => m.id === yk.parent_id);
    // Sadece meclis altindaki YK'lari goster (donem altindakileri degil)
    if (!parent) return;
    const parentName = parent.name;
    if (!ykByMeclis[parentName]) ykByMeclis[parentName] = [];
    ykByMeclis[parentName].push(yk);
  });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Kullanici Duzenle</DialogTitle>
          <DialogDescription>{u.name} {u.surname} - {u.email}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Ad</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="edit-name" /></div>
            <div><Label>Soyad</Label><Input value={form.surname} onChange={e => setForm(f => ({ ...f, surname: e.target.value }))} data-testid="edit-surname" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Telefon</Label><Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} data-testid="edit-phone" /></div>
            <div><Label>TC Kimlik No</Label><Input value={form.tc_kimlik} maxLength={11} onChange={e => setForm(f => ({ ...f, tc_kimlik: e.target.value.replace(/\D/g, '') }))} data-testid="edit-tc" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Dogum Tarihi</Label><Input type="date" value={form.dogum_tarihi} onChange={e => setForm(f => ({ ...f, dogum_tarihi: e.target.value }))} data-testid="edit-dogum" /></div>
            <div><Label>Cinsiyet</Label>
              <Select value={form.cinsiyet || 'none'} onValueChange={v => setForm(f => ({ ...f, cinsiyet: v === 'none' ? '' : v }))}>
                <SelectTrigger data-testid="edit-cinsiyet"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Secilmedi</SelectItem>
                  <SelectItem value="erkek">Erkek</SelectItem>
                  <SelectItem value="kadin">Kadin</SelectItem>
                  <SelectItem value="belirtmek_istemiyor">Belirtmek Istemiyor</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Egitim Durumu</Label>
            <Select value={form.egitim_durumu || 'none'} onValueChange={v => setForm(f => ({ ...f, egitim_durumu: v === 'none' ? '' : v }))}>
              <SelectTrigger data-testid="edit-egitim"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Secilmedi</SelectItem>
                {Object.entries(EGITIM_MAP).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Meslek</Label>
            <Input
              value={form.meslek || ''}
              onChange={e => setForm(f => ({ ...f, meslek: e.target.value }))}
              placeholder="Örn: Öğretmen, Mühendis..."
              maxLength={100}
              data-testid="edit-meslek"
            />
          </div>
          <div><Label>Mezun/Devam Edilen Okul (opsiyonel)</Label>
            <Input
              value={form.okul_adi || ''}
              onChange={e => setForm(f => ({ ...f, okul_adi: e.target.value }))}
              placeholder="Örn: Çukurova Üniversitesi, Adana Anadolu Lisesi..."
              maxLength={150}
              data-testid="edit-okul-adi"
            />
          </div>
          <div><Label>Üyesi/Gönüllüsü Olduğu Kuruluş (opsiyonel)</Label>
            <Input
              value={form.uyesi_oldugu_kurulus || ''}
              onChange={e => setForm(f => ({ ...f, uyesi_oldugu_kurulus: e.target.value }))}
              placeholder="Örn: Adana Doğa Derneği, Yeşilay..."
              maxLength={150}
              data-testid="edit-uye-kurulus"
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Il</Label>
              <Select value={form.il_id || 'none'} onValueChange={v => v === 'none' ? setForm(f => ({ ...f, il_id: '' })) : handleIlChange(v)}>
                <SelectTrigger><SelectValue placeholder="Il" /></SelectTrigger>
                <SelectContent>{iller.map(il => <SelectItem key={il.id} value={il.id}>{il.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Ilce</Label>
              <Select value={form.ilce_id || 'none'} onValueChange={v => v === 'none' ? setForm(f => ({ ...f, ilce_id: '' })) : handleIlceChange(v)}>
                <SelectTrigger><SelectValue placeholder="Ilce" /></SelectTrigger>
                <SelectContent>{ilceler.map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Mahalle</Label>
              <Select value={form.mahalle_id || 'none'} onValueChange={v => setForm(f => ({ ...f, mahalle_id: v === 'none' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="Mahalle" /></SelectTrigger>
                <SelectContent>{mahalleler.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Acik Adres</Label><Textarea rows={2} value={form.acik_adres} onChange={e => setForm(f => ({ ...f, acik_adres: e.target.value }))} data-testid="edit-adres" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Rol</Label>
              <Select value={form.role} onValueChange={handleRoleChange}>
                <SelectTrigger data-testid="edit-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(USER_ROLES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Durum</Label>
              <Select value={form.is_active ? 'true' : 'false'} onValueChange={v => setForm(f => ({ ...f, is_active: v === 'true' }))}>
                <SelectTrigger data-testid="edit-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">Aktif</SelectItem>
                  <SelectItem value="false">Pasif</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Organ Atamalari - Bagimsiz */}
          <div className="border-t pt-3 mt-1">
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="w-4 h-4 text-blue-600" />
              <Label className="text-sm font-semibold text-slate-700">Organ Atamalari</Label>
            </div>
            <div className="grid grid-cols-1 gap-3">
              {/* Meclis Baskanligi */}
              <div>
                <Label className="text-xs text-slate-500">Meclis Baskanligi</Label>
                <Select value={selectedMeclis || 'none'} onValueChange={v => setSelectedMeclis(v === 'none' ? '' : v)}>
                  <SelectTrigger data-testid="edit-meclis"><SelectValue placeholder="Meclis seciniz" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Atanmadi</SelectItem>
                    {meclisler.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {/* CG Baskanligi - Bagimsiz */}
              <div>
                <Label className="text-xs text-slate-500">Calisma Grubu Baskanligi</Label>
                <Select value={selectedCG || 'none'} onValueChange={v => setSelectedCG(v === 'none' ? '' : v)}>
                  <SelectTrigger data-testid="edit-cg"><SelectValue placeholder="CG seciniz" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Atanmadi</SelectItem>
                    {Object.entries(cgByMeclis).map(([meclisName, cgs]) => (
                      <React.Fragment key={meclisName}>
                        <SelectItem value={`header_${meclisName}`} disabled className="text-xs font-semibold text-slate-400 bg-slate-50">
                          {meclisName}
                        </SelectItem>
                        {cgs.map(cg => <SelectItem key={cg.id} value={cg.id}>{cg.name}</SelectItem>)}
                      </React.Fragment>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* Yurutme Kurulu Uyeligi - Meclis bazli */}
              <div>
                <Label className="text-xs text-slate-500">Yurutme Kurulu Uyeligi</Label>
                <Select value={selectedYK || 'none'} onValueChange={v => setSelectedYK(v === 'none' ? '' : v)}>
                  <SelectTrigger data-testid="edit-yk"><SelectValue placeholder="Meclis YK seciniz" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Atanmadi</SelectItem>
                    {Object.entries(ykByMeclis).map(([meclisName, yks]) => (
                      <React.Fragment key={meclisName}>
                        <SelectItem value={`header_yk_${meclisName}`} disabled className="text-xs font-semibold text-slate-400 bg-slate-50">
                          {meclisName}
                        </SelectItem>
                        {yks.map(yk => <SelectItem key={yk.id} value={yk.id}>{yk.name}</SelectItem>)}
                      </React.Fragment>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-slate-400 mt-1">
                  Secilen meclisin Yurutme Kuruluna uye yapilir; rol otomatik "Yurutme Kurulu Uyesi" olur.
                </p>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)}>Iptal</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-user-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Shield className="w-4 h-4 mr-1" />} Kaydet
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Ana Sayfa
const UsersListPage = () => {
  const { user } = useAuth();
  usePageTitle('Kullanicilar');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get('q') || '');
  const [roleFilter, setRoleFilter] = useState('');
  const [ilFilter, setIlFilter] = useState('');
  const [ilceFilter, setIlceFilter] = useState('');
  const [mahalleFilter, setMahalleFilter] = useState('');
  const [detailUser, setDetailUser] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [iller, setIller] = useState([]);
  const [filterIlceler, setFilterIlceler] = useState([]);
  const [filterMahalleler, setFilterMahalleler] = useState([]);
  // KVKK iletisim izni filtreleri
  const [izinEmail, setIzinEmail] = useState(false);
  const [izinSms, setIzinSms] = useState(false);
  const [izinVoice, setIzinVoice] = useState(false);
  const [izinMatch, setIzinMatch] = useState('any'); // 'any' veya 'all'
  // Toplu e-posta (Users)
  const [bulkEmailOpen, setBulkEmailOpen] = useState(false);
  const [bulkSubject, setBulkSubject] = useState('');
  const [bulkBody, setBulkBody] = useState('<p>Sayın {{ad_soyad}},</p>\n<p>Merhaba,</p>\n<p>... toplantı içeriği ...</p>\n<p>Saygılarımızla,<br/>Kent Konseyi</p>');
  const [bulkRequireConsent, setBulkRequireConsent] = useState(true);
  const [bulkSending, setBulkSending] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyDetail, setHistoryDetail] = useState(null);
  const [pendingKkCount, setPendingKkCount] = useState(0);
  const limit = 20;

  useEffect(() => {
    locationsAPI.getIller().then(r => {
      setIller(r.data);
      // Adana is default, load its ilceler
      const adana = r.data.find(i => i.name === 'Adana') || r.data[0];
      if (adana) {
        setIlFilter(adana.id);
        locationsAPI.getIlceler(adana.id).then(r2 => setFilterIlceler(r2.data)).catch(() => {});
      }
    }).catch(() => {});
    // Onay bekleyen sayisini yukle (admin ve yetkili roller icin)
    if (['admin', 'kent_konseyi_baskani', 'genel_sekreter', 'genel_sekreter_yardimcisi'].includes(user?.role)) {
      usersAPI.listPendingKkApproval({ limit: 1 })
        .then(r => setPendingKkCount(r.data.total || 0))
        .catch(() => {});
    }
  }, [user?.role]);

  const handleIlFilterChange = async (v) => {
    const val = v === 'all' ? '' : v;
    setIlFilter(val);
    setIlceFilter('');
    setMahalleFilter('');
    setFilterMahalleler([]);
    setPage(1);
    if (val) {
      try { const r = await locationsAPI.getIlceler(val); setFilterIlceler(r.data); }
      catch { setFilterIlceler([]); }
    } else {
      setFilterIlceler([]);
    }
  };

  useEffect(() => { loadUsers(); }, [page, roleFilter, ilFilter, ilceFilter, mahalleFilter, izinEmail, izinSms, izinVoice, izinMatch]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const params = {
        skip: (page - 1) * limit, limit,
        search: searchTerm || undefined,
        role: roleFilter || undefined,
        il_id: ilFilter || undefined,
        ilce_id: ilceFilter || undefined,
        mahalle_id: mahalleFilter || undefined,
        izin_email: izinEmail || undefined,
        izin_sms: izinSms || undefined,
        izin_voice: izinVoice || undefined,
        izin_match: (izinEmail || izinSms || izinVoice) ? izinMatch : undefined,
      };
      const r = await usersAPI.getAll(params);
      setUsers(r.data.users);
      setTotal(r.data.total);
    } catch { toast.error('Kullanicilar yuklenemedi'); }
    finally { setLoading(false); }
  };

  const handleIlceFilterChange = async (v) => {
    const val = v === 'all' ? '' : v;
    setIlceFilter(val);
    setMahalleFilter('');
    setFilterMahalleler([]);
    setPage(1);
    if (val) {
      try { const r = await locationsAPI.getMahalleler(val); setFilterMahalleler(r.data); } catch { setFilterMahalleler([]); }
    }
  };

  const handleSearch = (e) => { e.preventDefault(); setPage(1); loadUsers(); };

  const handleDeleteUser = async (userId) => {
    if (!confirm('Bu kullaniciyi silmek istediginizden emin misiniz?')) return;
    try { await usersAPI.delete(userId); toast.success('Kullanici silindi'); loadUsers(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Silme basarisiz'); }
  };

  const handleExportAllExcel = async () => {
    try {
      toast.info('Tum kullanicilar Excel\'e hazirlaniyor...');
      const params = {
        search: searchTerm || undefined,
        role: roleFilter || undefined,
        il_id: ilFilter || undefined,
        ilce_id: ilceFilter || undefined,
        mahalle_id: mahalleFilter || undefined,
      };
      const r = await usersAPI.exportAllExcel(params);
      const blob = new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url; a.download = `tum_kullanicilar_${stamp}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('Tum kullanicilar Excel olarak indirildi');
    } catch (e) {
      toast.error('Excel aktarim basarisiz: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleExportKvkkExcel = async () => {
    try {
      toast.info('KVKK izinli kullanicilar hazirlaniyor...');
      const params = {
        search: searchTerm || undefined,
        role: roleFilter || undefined,
        il_id: ilFilter || undefined,
        ilce_id: ilceFilter || undefined,
        mahalle_id: mahalleFilter || undefined,
        izin_email: izinEmail || undefined,
        izin_sms: izinSms || undefined,
        izin_voice: izinVoice || undefined,
        izin_match: (izinEmail || izinSms || izinVoice) ? izinMatch : undefined,
      };
      const r = await usersAPI.exportKvkkExcel(params);
      const blob = new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url; a.download = `kvkk_izinli_kullanicilar_${stamp}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('KVKK izinli kullanicilar Excel olarak indirildi');
    } catch (e) {
      toast.error('KVKK Excel aktarim basarisiz: ' + (e.response?.data?.detail || e.message));
    }
  };

  const currentBulkFilters = () => ({
    search: searchTerm || undefined,
    role: roleFilter || undefined,
    il_id: ilFilter || undefined,
    ilce_id: ilceFilter || undefined,
    mahalle_id: mahalleFilter || undefined,
    izin_email: izinEmail || undefined,
    izin_sms: izinSms || undefined,
    izin_voice: izinVoice || undefined,
    izin_match: (izinEmail || izinSms || izinVoice) ? izinMatch : undefined,
  });

  const handleSendBulkEmail = async () => {
    if (!bulkSubject.trim()) { toast.error('Konu zorunlu'); return; }
    if (!bulkBody.trim()) { toast.error('Icerik zorunlu'); return; }
    setBulkSending(true);
    try {
      const r = await usersAPI.sendBulkEmail({
        subject: bulkSubject,
        body_html: bulkBody,
        require_email_consent: bulkRequireConsent,
        ...currentBulkFilters(),
      });
      toast.success(r.data.message);
      setBulkEmailOpen(false);
      setBulkSubject('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Toplu gonderim basarisiz');
    } finally {
      setBulkSending(false);
    }
  };

  const openBulkHistory = async () => {
    setHistoryOpen(true);
    try {
      const r = await usersAPI.getBulkEmailHistory({ limit: 50 });
      setHistoryItems(r.data.items || []);
    } catch { toast.error('Gecmis yuklenemedi'); }
  };

  const getRoleBadge = (role) => {
    const roleInfo = USER_ROLES[role] || { label: role, color: 'default' };
    const cls = {
      success: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      warning: 'bg-amber-50 text-amber-700 border-amber-200',
      error: 'bg-red-50 text-red-700 border-red-200',
      info: 'bg-blue-50 text-blue-700 border-blue-200',
      default: 'bg-slate-50 text-slate-700 border-slate-200',
    }[roleInfo.color] || 'bg-slate-50 text-slate-700 border-slate-200';
    return <Badge className={`${cls} border text-xs`}>{roleInfo.label}</Badge>;
  };

  const totalPages = Math.ceil(total / limit);
  const isAdmin = user?.role === 'admin' || user?.role === 'genel_sekreter';

  // Stats
  const roleCounts = {};
  users.forEach(u => { roleCounts[u.role] = (roleCounts[u.role] || 0) + 1; });

  return (
    <div className="space-y-5" data-testid="users-list-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Kullanici Yonetimi</h1>
          <p className="text-sm text-slate-500">Toplam {total} kayitli kullanici</p>
        </div>
        <div className="flex items-center gap-2">
          {['admin', 'kent_konseyi_baskani', 'genel_sekreter', 'genel_sekreter_yardimcisi'].includes(user?.role) && (
            <Link to="/panel/kullanicilar/onay-bekleyen">
              <Button variant="outline" size="sm" className="border-amber-300 text-amber-800 hover:bg-amber-50 relative" data-testid="pending-kk-btn">
                <UserCheck className="w-4 h-4 mr-1.5" />
                Onay Bekleyen
                {pendingKkCount > 0 && (
                  <Badge className="ml-2 bg-amber-500 text-white border-0 h-5 px-1.5 text-xs">{pendingKkCount}</Badge>
                )}
              </Button>
            </Link>
          )}
          {isAdmin && (
            <>
              <Button variant="default" size="sm" onClick={() => setBulkEmailOpen(true)} data-testid="bulk-email-btn"
                className="bg-indigo-600 hover:bg-indigo-700">
                <Mail className="w-4 h-4 mr-1.5" /> Toplu E-posta
              </Button>
              <Button variant="outline" size="sm" onClick={openBulkHistory} data-testid="email-history-btn">
                <Clock className="w-4 h-4 mr-1.5" /> Gecmis
              </Button>
            </>
          )}
          <Button variant="outline" size="sm" onClick={handleExportAllExcel} data-testid="export-all-excel-btn" className="text-slate-700">
            <Download className="w-4 h-4 mr-1.5" /> Tum Kullanicilar Excel
          </Button>
          <Button variant="default" size="sm" onClick={handleExportKvkkExcel} data-testid="export-kvkk-excel-btn"
            className="bg-emerald-600 hover:bg-emerald-700">
            <Download className="w-4 h-4 mr-1.5" /> KVKK Izinli Excel
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="shadow-sm">
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="space-y-3">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input placeholder="Ad, soyad, e-posta, TC veya telefon ile ara..." value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)} className="pl-9" data-testid="user-search-input" />
              </div>
              <Button type="submit" variant="secondary" size="sm"><Filter className="w-4 h-4 mr-1" /> Ara</Button>
              {(searchTerm || roleFilter || ilFilter || ilceFilter || mahalleFilter) && (
                <Button type="button" variant="ghost" size="sm" onClick={() => { setSearchTerm(''); setRoleFilter(''); setIlFilter(''); setIlceFilter(''); setMahalleFilter(''); setFilterIlceler([]); setFilterMahalleler([]); setPage(1); setTimeout(loadUsers, 0); }}>
                  <X className="w-4 h-4 mr-1" /> Temizle
                </Button>
              )}
            </div>
            <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
              <Select value={roleFilter || 'all'} onValueChange={v => { setRoleFilter(v === 'all' ? '' : v); setPage(1); }}>
                <SelectTrigger className="w-full sm:w-40" data-testid="role-filter"><SelectValue placeholder="Rol" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tum Roller</SelectItem>
                  {Object.entries(USER_ROLES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={ilFilter || 'all'} onValueChange={handleIlFilterChange}>
                <SelectTrigger className="w-full sm:w-40" data-testid="il-filter"><SelectValue placeholder="Il" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tum Iller</SelectItem>
                  {iller.map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={ilceFilter || 'all'} onValueChange={handleIlceFilterChange} disabled={!ilFilter && filterIlceler.length === 0}>
                <SelectTrigger className="w-full sm:w-44" data-testid="ilce-filter"><SelectValue placeholder="Ilce" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tum Ilceler</SelectItem>
                  {filterIlceler.map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {filterMahalleler.length > 0 && (
                <Select value={mahalleFilter || 'all'} onValueChange={v => { setMahalleFilter(v === 'all' ? '' : v); setPage(1); }}>
                  <SelectTrigger className="w-full sm:w-44" data-testid="mahalle-filter"><SelectValue placeholder="Mahalle" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tum Mahalleler</SelectItem>
                    {filterMahalleler.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* KVKK Iletisim Izni Filtreleri */}
            <div className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-3 pt-2 border-t border-slate-100">
              <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">KVKK Izin Filtresi:</span>
              <label className="flex items-center gap-1.5 text-sm cursor-pointer" data-testid="filter-izin-email">
                <input type="checkbox" checked={izinEmail} onChange={e => { setIzinEmail(e.target.checked); setPage(1); }}
                  className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                <span className="text-blue-700 font-medium">E-posta</span>
              </label>
              <label className="flex items-center gap-1.5 text-sm cursor-pointer" data-testid="filter-izin-sms">
                <input type="checkbox" checked={izinSms} onChange={e => { setIzinSms(e.target.checked); setPage(1); }}
                  className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
                <span className="text-emerald-700 font-medium">SMS</span>
              </label>
              <label className="flex items-center gap-1.5 text-sm cursor-pointer" data-testid="filter-izin-voice">
                <input type="checkbox" checked={izinVoice} onChange={e => { setIzinVoice(e.target.checked); setPage(1); }}
                  className="w-4 h-4 rounded border-slate-300 text-purple-600 focus:ring-purple-500" />
                <span className="text-purple-700 font-medium">Sesli Arama</span>
              </label>
              {(izinEmail || izinSms || izinVoice) && (
                <>
                  <div className="flex items-center gap-1 text-xs text-slate-500">
                    <span>Eslestirme:</span>
                    <Select value={izinMatch} onValueChange={v => { setIzinMatch(v); setPage(1); }}>
                      <SelectTrigger className="w-28 h-7 text-xs" data-testid="izin-match-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Herhangi biri</SelectItem>
                        <SelectItem value="all">Tumu</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button type="button" variant="ghost" size="sm"
                    onClick={() => { setIzinEmail(false); setIzinSms(false); setIzinVoice(false); setIzinMatch('any'); setPage(1); }}
                    className="h-7 text-xs" data-testid="clear-kvkk-filter-btn">
                    <X className="w-3 h-3 mr-1" /> KVKK Filtresini Temizle
                  </Button>
                </>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card className="shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>
          ) : users.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50/80 hover:bg-slate-50/80">
                      <TableHead className="font-semibold text-xs">Kullanici</TableHead>
                      <TableHead className="font-semibold text-xs">TC Kimlik</TableHead>
                      <TableHead className="font-semibold text-xs">Telefon</TableHead>
                      <TableHead className="font-semibold text-xs">Ilce</TableHead>
                      <TableHead className="font-semibold text-xs">Mahalle</TableHead>
                      <TableHead className="font-semibold text-xs">Acik Adres</TableHead>
                      <TableHead className="font-semibold text-xs">Dogum Tarihi</TableHead>
                      <TableHead className="font-semibold text-xs">Cinsiyet</TableHead>
                      <TableHead className="font-semibold text-xs">Egitim</TableHead>
                      <TableHead className="font-semibold text-xs">Rol</TableHead>
                      <TableHead className="font-semibold text-xs">Organ Atamasi</TableHead>
                      <TableHead className="font-semibold text-xs">Durum</TableHead>
                      <TableHead className="font-semibold text-xs text-right">Islemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map(u => (
                      <TableRow key={u.id} className="hover:bg-slate-50/50 cursor-pointer" onClick={() => { setDetailUser(u); setDetailOpen(true); }} data-testid={`user-row-${u.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-2.5">
                            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shrink-0">
                              <span className="text-xs font-bold text-white">{u.name?.charAt(0)}{u.surname?.charAt(0)}</span>
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-sm text-slate-900 truncate">{u.name} {u.surname}</p>
                              <p className="text-xs text-slate-400 truncate">{u.email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-slate-600 font-mono">{u.tc_kimlik || <span className="text-slate-300">-</span>}</TableCell>
                        <TableCell className="text-sm text-slate-600">{u.phone || <span className="text-slate-300">-</span>}</TableCell>
                        <TableCell className="text-sm text-slate-600">{u.ilce_name || <span className="text-slate-300">-</span>}</TableCell>
                        <TableCell className="text-sm text-slate-600">{u.mahalle_name || <span className="text-slate-300">-</span>}</TableCell>
                        <TableCell className="text-sm text-slate-600 max-w-[200px] truncate">{u.acik_adres || <span className="text-slate-300">-</span>}</TableCell>
                        <TableCell className="text-sm text-slate-600">
                          {u.dogum_tarihi ? new Date(u.dogum_tarihi).toLocaleDateString('tr-TR') : <span className="text-slate-300">-</span>}
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">{CINSIYET_MAP[u.cinsiyet] || <span className="text-slate-300">-</span>}</TableCell>
                        <TableCell className="text-sm text-slate-600">{EGITIM_MAP[u.egitim_durumu] || <span className="text-slate-300">-</span>}</TableCell>
                        <TableCell>
                          {getRoleBadge(u.role)}
                          {u.effective_role && u.effective_role !== u.role && (
                            <div className="mt-0.5">
                              <Badge variant="outline" className="text-[10px] border-purple-200 text-purple-700 bg-purple-50">
                                Etkin: {USER_ROLES[u.effective_role]?.label || u.effective_role}
                              </Badge>
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          {(u.organ_assignments || []).length > 0 ? (
                            <div className="space-y-0.5">
                              {(u.organ_assignments || []).map((a, i) => {
                                const prefix = a.type === 'meclis'
                                  ? (a.role === 'baskan' ? 'MB' : 'M-Uye')
                                  : a.type === 'calisma_grubu'
                                  ? (a.role === 'baskan' ? 'CG-Bsk' : 'CG-Uye')
                                  : a.type === 'yurutme_kurulu'
                                  ? (a.role === 'baskan' ? 'YK-Bsk' : 'YK-Uye')
                                  : a.type.slice(0, 3).toUpperCase();
                                const cls = a.role === 'uye'
                                  ? 'border-emerald-200 text-emerald-700 bg-emerald-50'
                                  : 'border-blue-200 text-blue-700 bg-blue-50';
                                return (
                                  <Badge key={a.id || `org-${i}`} variant="outline" className={`text-xs ${cls} mr-1`}>
                                    {prefix}: {a.name}
                                  </Badge>
                                );
                              })}
                            </div>
                          ) : <span className="text-slate-300 text-xs">-</span>}
                        </TableCell>
                        <TableCell>
                          <Badge variant={u.is_active ? 'outline' : 'secondary'} className={u.is_active ? 'border-emerald-200 text-emerald-700 bg-emerald-50 text-xs' : 'text-xs'}>
                            {u.is_active ? 'Aktif' : 'Pasif'}
                          </Badge>
                          {u.is_verified === false && (
                            <Badge variant="outline" className="border-amber-200 text-amber-700 bg-amber-50 text-xs ml-1">
                              E-posta Bekleniyor
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                          <div className="flex justify-end gap-0.5">
                            <Button variant="ghost" size="sm" onClick={() => { setDetailUser(u); setDetailOpen(true); }} data-testid={`view-user-${u.id}`}>
                              <Eye className="w-4 h-4 text-slate-500" />
                            </Button>
                            {isAdmin && (
                              <>
                                <Button variant="ghost" size="sm" onClick={() => { setEditUser(u); setEditOpen(true); }} data-testid={`edit-user-${u.id}`}>
                                  <Edit className="w-4 h-4 text-slate-500" />
                                </Button>
                                {u.id !== user?.id && (
                                  <Button variant="ghost" size="sm" onClick={() => handleDeleteUser(u.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`delete-user-${u.id}`}>
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <p className="text-sm text-slate-500">Toplam {total} kayit, Sayfa {page}/{totalPages}</p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} data-testid="prev-page">
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const start = Math.max(1, Math.min(page - 2, totalPages - 4));
                      const pg = start + i;
                      if (pg > totalPages) return null;
                      return (
                        <Button key={pg} variant={pg === page ? 'default' : 'outline'} size="sm" onClick={() => setPage(pg)} className="w-9">
                          {pg}
                        </Button>
                      );
                    })}
                    <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} data-testid="next-page">
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Users className="w-12 h-12 text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900">Kullanici bulunamadi</h3>
              <p className="text-slate-500 mt-1">Arama kriterlerini degistirin</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <UserDetailDialog
        user={detailUser} open={detailOpen} onClose={setDetailOpen}
        onEdit={u => { setEditUser(u); setEditOpen(true); }}
        iller={iller} isAdmin={isAdmin}
      />

      {/* Edit Dialog */}
      <UserEditDialog
        user={editUser} open={editOpen} onClose={setEditOpen}
        onSaved={loadUsers} iller={iller}
      />

      {/* Toplu E-posta Dialog */}
      <Dialog open={bulkEmailOpen} onOpenChange={setBulkEmailOpen}>
        <DialogContent className="max-w-2xl" data-testid="bulk-email-dialog">
          <DialogHeader>
            <DialogTitle>Toplu E-posta Gonder</DialogTitle>
            <DialogDescription>
              Aktif filtrelere uyan {total} kullaniciya e-posta gonderilecek. Icerikte <code>{'{{ad}}'}</code>, <code>{'{{soyad}}'}</code>, <code>{'{{ad_soyad}}'}</code> degiskenlerini kullanabilirsiniz.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Konu *</Label>
              <Input value={bulkSubject} onChange={(e) => setBulkSubject(e.target.value)} placeholder="E-posta konusu" data-testid="bulk-email-subject" />
            </div>
            <div>
              <Label>Icerik (HTML) *</Label>
              <Textarea value={bulkBody} onChange={(e) => setBulkBody(e.target.value)} rows={10} className="font-mono text-xs" data-testid="bulk-email-body" />
              <p className="text-[11px] text-slate-500 mt-1">Degiskenler: <code>{'{{ad}}'}</code>, <code>{'{{soyad}}'}</code>, <code>{'{{ad_soyad}}'}</code></p>
            </div>
            <label className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-800 cursor-pointer" data-testid="require-consent-toggle">
              <input type="checkbox" checked={bulkRequireConsent} onChange={(e) => setBulkRequireConsent(e.target.checked)}
                className="w-4 h-4 rounded border-amber-400 text-amber-600 focus:ring-amber-500" />
              <span>
                <b>KVKK E-posta izni zorunlu:</b> Sadece e-posta iletisim izni veren kullanicilara gonder (onerilir).
              </span>
            </label>
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs text-slate-700">
              <div className="font-medium mb-1">Kullanilacak filtreler:</div>
              <ul className="list-disc list-inside space-y-0.5">
                {roleFilter && <li>Rol: <b>{USER_ROLES[roleFilter]?.label || roleFilter}</b></li>}
                {searchTerm && <li>Arama: <b>{searchTerm}</b></li>}
                {ilFilter && <li>Il: <b>{iller.find(i => i.id === ilFilter)?.name || ilFilter}</b></li>}
                {ilceFilter && <li>Ilce: <b>{filterIlceler.find(i => i.id === ilceFilter)?.name || ilceFilter}</b></li>}
                {mahalleFilter && <li>Mahalle: <b>{filterMahalleler.find(m => m.id === mahalleFilter)?.name || mahalleFilter}</b></li>}
                {(izinEmail || izinSms || izinVoice) && <li>KVKK: {izinEmail && 'E-posta '}{izinSms && 'SMS '}{izinVoice && 'Sesli '} ({izinMatch === 'all' ? 'tumu' : 'en az biri'})</li>}
                {!roleFilter && !searchTerm && !ilFilter && !ilceFilter && !mahalleFilter && !(izinEmail||izinSms||izinVoice) && <li>Filtre yok - <b>tum kullanicilar</b></li>}
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkEmailOpen(false)} disabled={bulkSending} data-testid="bulk-email-cancel">
              Iptal
            </Button>
            <Button onClick={handleSendBulkEmail} disabled={bulkSending} className="bg-indigo-600 hover:bg-indigo-700" data-testid="bulk-email-send">
              {bulkSending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Gonderiliyor...</> : <><Send className="w-4 h-4 mr-2" /> Gonder</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Gecmis Dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="email-history-dialog">
          <DialogHeader>
            <DialogTitle>Toplu E-posta Gecmisi</DialogTitle>
            <DialogDescription>Son 50 gonderim</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {historyItems.length === 0 && <p className="text-sm text-slate-500 py-6 text-center">Henuz gonderim yok</p>}
            {historyItems.map((h) => (
              <Card key={h.id} className="border border-slate-200">
                <CardContent className="p-3 flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm truncate">{h.subject}</div>
                    <div className="text-xs text-slate-500 flex gap-3 flex-wrap mt-1">
                      <span>Alici: <b>{h.recipient_count}</b></span>
                      <span className="text-green-600">Basarili: {h.sent_count}</span>
                      {h.failed_count > 0 && <span className="text-red-600">Basarisiz: {h.failed_count}</span>}
                      {h.skipped_no_consent > 0 && <span className="text-amber-600">KVKK Atlanan: {h.skipped_no_consent}</span>}
                      <span>{formatDateTime(h.sent_at)}</span>
                      {h.sent_by_name && <span>Gonderen: {h.sent_by_name}</span>}
                    </div>
                  </div>
                  <Button size="sm" variant="outline"
                    onClick={() => { setHistoryOpen(false); setHistoryDetail(h); }}
                    data-testid={`history-detail-${h.id}`}>
                    <Eye className="w-3.5 h-3.5 mr-1" /> Detay
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Gecmis Detay Dialog */}
      <Dialog open={!!historyDetail} onOpenChange={(o) => { if (!o) { setHistoryDetail(null); setHistoryOpen(true); } }}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="email-history-detail-dialog">
          <DialogHeader>
            <DialogTitle>{historyDetail?.subject}</DialogTitle>
            <DialogDescription>{historyDetail && formatDateTime(historyDetail.sent_at)} - {historyDetail?.sent_by_name}</DialogDescription>
          </DialogHeader>
          {historyDetail && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-4 gap-2 text-xs">
                <div className="bg-slate-50 p-2 rounded"><b>Alici:</b> {historyDetail.recipient_count}</div>
                <div className="bg-green-50 p-2 rounded"><b>Basarili:</b> {historyDetail.sent_count}</div>
                <div className="bg-red-50 p-2 rounded"><b>Basarisiz:</b> {historyDetail.failed_count}</div>
                <div className="bg-amber-50 p-2 rounded"><b>KVKK Atlanan:</b> {historyDetail.skipped_no_consent || 0}</div>
              </div>
              <div>
                <Label className="text-xs">Icerik</Label>
                <div className="border rounded p-3 bg-white max-h-60 overflow-auto text-xs" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(historyDetail.body_html || '') }} />
              </div>
              <div>
                <Label className="text-xs">Alici E-postalari ({historyDetail.recipient_emails?.length || 0})</Label>
                <div className="border rounded p-2 bg-slate-50 max-h-40 overflow-auto text-xs font-mono">
                  {(historyDetail.recipient_emails || []).join(', ')}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export { UsersListPage };
